import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Container, UncontrolledTooltip } from "reactstrap";
import { format } from "date-fns";
import {
    faPowerOff,
    faBriefcaseMedical,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import HealthReportModal from "./Components/HealthReportModal/HealthReportModal";
import RetireModal from "Pages/Admin/ShuttleStatus/Components/RetireModal/RetireModal";
import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Admin/ShuttleStatus/ShuttleStatus.module.css";
import usePageTitle from "Hooks/usePageTitle";

export interface IShuttleStatus {
    company_id: string;
    machine_name: string;
    version: string;
    tag: string;
    companies_to_process: string;
    earliest_connection: string;
    latest_connection: string;
    declared_lost: string | null;
    retired: boolean;
}

export type ModalType = "retire" | "health" | "";
export interface IModal {
    name: ModalType;
    row: IShuttleStatus;
}

export default function ShuttleStatus() {
    const [statuses, setStatuses] = useState<IShuttleStatus[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [modal, setModal] = useState<IModal>();

    usePageTitle("Shuttle Status");
    const { get } = useFetch();
    const ignore = useRef(false);

    const getStatuses = useCallback(async () => {
        setIsLoading(true);

        const response = await get("/api/shuttle_status/all");

        let data: any;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log(data);

        if (ignore.current) return;

        if (response.ok) {
            setStatuses(data);
        }

        setIsLoading(false);
    }, [get]);

    // Get shuttle statuses
    useEffect(() => {
        ignore.current = false;

        getStatuses();

        return () => {
            ignore.current = true;
        };
    }, [get, getStatuses]);

    const onSetModal = (name?: ModalType, row?: IShuttleStatus) => {
        if (!row || !name) {
            setModal(undefined);
        } else {
            setModal({ name, row });
        }
    };

    const formatDate = ({ value }: { value: string }) =>
        format(new Date(value), "MMM dd, yyyy HH:mm");

    const getActiveCell = ({ value, row }: { value: boolean; row: any }) => {
        const color = row.original.retired
            ? "info"
            : value
            ? "success"
            : "danger";
        const text = row.original.retired ? "Retired" : value ? "Yes" : "No";

        return (
            <Badge className={styles.badge} color={color} pill>
                {text}
            </Badge>
        );
    };

    const columns = [
        {
            Header: "Company",
            accessor: "company_id",
        },
        {
            Header: "Server Name",
            accessor: "machine_name",
        },
        {
            Header: "Active",
            accessor: "active",
            Cell: getActiveCell,
            width: 75,
        },
        {
            Header: "Tag",
            accessor: "tag",
            width: 125,
        },
        {
            Header: "Companies Handled",
            accessor: "companies_to_process",
            width: 175,
        },
        {
            Header: "Shuttle Version",
            accessor: "version",
            width: 125,
        },
        {
            Header: "Last Connection",
            accessor: "latest_connection",
            Cell: formatDate,
        },
        {
            Header: "First Connection",
            accessor: "earliest_connection",
            Cell: formatDate,
        },
        {
            Header: "Health Report",
            width: 75,
            Cell: ({ row }: { row: any }) => {
                return row.values.active ? (
                    <>
                        <FontAwesomeIcon
                            id={`health-${row.index}`}
                            onClick={() =>
                                setModal({ name: "health", row: row.original })
                            }
                            className={styles.icon}
                            icon={faBriefcaseMedical}
                        />
                        <UncontrolledTooltip target={`health-${row.index}`}>
                            {`Health report for ${row.original.machine_name}`}
                        </UncontrolledTooltip>
                    </>
                ) : null;
            },
        },
        {
            Header: "Retire",
            width: 75,
            Cell: ({ row }: { row: any }) => (
                <>
                    {" "}
                    <FontAwesomeIcon
                        id={`retire-${row.index}`}
                        onClick={() =>
                            setModal({ name: "retire", row: row.original })
                        }
                        className={styles.icon}
                        icon={faPowerOff}
                    />
                    <UncontrolledTooltip target={`retire-${row.index}`}>
                        {`${row.original.retired ? "Un-retire" : "Retire"} ${
                            row.original.machine_name
                        }`}
                    </UncontrolledTooltip>
                </>
            ),
        },
    ];

    const tableProps = {
        data: statuses,
        columns: columns,
        showPagination: true,
        isLoading,
    };

    const retireModalProps = {
        getStatuses,
        modal,
        onSetModal,
    };

    const healthReportModalProps = {
        modal,
        onSetModal,
    };

    return (
        <Container fluid>
            <h1>Shuttle Status</h1>
            <ReactTable {...tableProps} />

            <RetireModal {...retireModalProps} />
            <HealthReportModal {...healthReportModalProps} />
        </Container>
    );
}
